import React, { memo } from 'react';
import { ExternalLink } from 'lucide-react';

import { AGING_THRESHOLDS } from 'components/logistics/fulfillmentLifecycle/constants';
import { cn } from 'shadcn/lib/utils';

/**
 * Maximum number of characters to display for an ID before truncating.
 */
const MAX_ID_LENGTH = 10;

/**
 * Truncate a string with an ellipsis if it exceeds the max length.
 *
 * @param {string} value
 * @param {number} [maxLength]
 * @returns {string}
 */
const truncateId = (value, maxLength = MAX_ID_LENGTH) => {
    if (!value) return '—';
    return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
};

/**
 * Return a Tailwind text-color class based on aging minutes.
 *
 * @param {number|null} agingMinutes
 * @returns {string}
 */
const getAgingColorClass = (agingMinutes) => {
    if (agingMinutes == null) return '';
    if (agingMinutes >= AGING_THRESHOLDS.CRITICAL) return 'text-red-600';
    if (agingMinutes >= AGING_THRESHOLDS.WARNING) return 'text-orange-500';
    return 'text-muted-foreground';
};

/**
 * Format aging minutes into a short human-readable string (e.g. "175m").
 *
 * @param {number|null} agingMinutes
 * @returns {string|null}
 */
const formatAgingShort = (agingMinutes) => {
    if (agingMinutes == null) return null;
    return `${agingMinutes}m`;
};

/**
 * Build the order deep dive URL for a given ID mode.
 *
 * @param {'order'|'shipment'} idMode
 * @param {string} orderId
 * @param {string} shipmentId
 * @returns {string}
 */
const buildDeepDiveUrl = (idMode, orderId, shipmentId) => {
    const param = idMode === 'shipment' ? `shipmentID=${shipmentId}` : `orderNumber=${orderId}`;
    return `/lettuce/system/order-deep-dive?${param}`;
};

/**
 * ShipmentChip — displays a single shipment as a compact chip that links to
 * the Order Deep Dive page.
 *
 * Shows a truncated Order ID or Shipment ID (controlled by `idMode`), the
 * aging duration with color-coding, and an external-link icon.
 *
 * @param {Object} props
 * @param {string} props.orderId - The order identifier
 * @param {string} props.shipmentId - The shipment identifier
 * @param {number|null} props.agingMinutes - Minutes since the relevant event, or null
 * @param {'order'|'shipment'} props.idMode - Which ID to display
 * @param {boolean} [props.informational] - When true, aging is displayed without warning/critical color-coding
 */
function ShipmentChip({ agingMinutes, idMode, informational, orderId, shipmentId }) {
    const displayId = idMode === 'shipment' ? shipmentId : orderId;
    const agingText = formatAgingShort(agingMinutes);
    const agingColor = informational ? 'text-muted-foreground' : getAgingColorClass(agingMinutes);

    return (
        <a
            href={buildDeepDiveUrl(idMode, orderId, shipmentId)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs hover:bg-muted/50 transition-colors cursor-pointer no-underline text-foreground"
            title={idMode === 'shipment' ? shipmentId : orderId}
        >
            <span className="font-medium">{truncateId(displayId)}</span>

            {agingText && <span className={cn('tabular-nums', agingColor)}>{agingText}</span>}

            <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
        </a>
    );
}

export default memo(ShipmentChip);
